import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { getAllusuarios, getEquipo, getAsignacion, postasigequi } from '../../../redux/actions';
import {
  Container, TextField, Button, Grid, Paper, Typography, Modal, Box,
  IconButton, FormControl, InputAdornment, Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import CloseIcon from '@mui/icons-material/Close';
import { createSelector } from 'reselect';
import {
  FaUserPlus,
  FaLaptop,
  FaCalendarAlt,
  FaCommentAlt,
  FaUser,
  FaSave,
  FaEye,
  FaSearch,
  FaFileExcel,
  FaClipboardList
} from 'react-icons/fa';
import styles from './Asignarequipos.module.css';

const selectUsuarios = createSelector(
  (state) => state.allusuarios.usuarios,
  (usuarios) => usuarios || []
);

const selectEquipos = createSelector(
  (state) => state.equipos,
  (equipos) => equipos || []
);

const selectAsignaciones = createSelector(
  (state) => state.asignaciones,
  (asignaciones) => asignaciones || []
);

const FormularioAsignacionEquipo = () => {
  const dispatch = useDispatch();
  const allusuarios = useSelector(selectUsuarios);
  const allequipos = useSelector(selectEquipos);
  const asignaciones = useSelector(selectAsignaciones) || [];

  const [open, setOpen] = useState(false);
  const [fechaAsignacion, setFechaAsignacion] = useState('');
  const [observacion, setObservacion] = useState('');
  const [UsuarioId, setUsuarioId] = useState(null);
  const [EquipoId, setEquipoId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(getAsignacion());
    dispatch(getAllusuarios());
    dispatch(getEquipo());
  }, [dispatch]);

  const handleOpen = () => {
    dispatch(getAsignacion());
    setSearchTerm('');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleExportExcel = () => {
    if (filteredAsignaciones.length === 0) {
      Swal.fire({ icon: 'info', title: 'Sin datos', text: 'No hay asignaciones para exportar.' });
      return;
    }

    const dataToExport = filteredAsignaciones.map(item => ({
      'Código Equipo': item.equipoCod,
      'Usuario': item.usuarioNombre,
      'Fecha Asignación': item.fechaAsignacion,
      'Serial': item.serialEquipo,
      'Tipo Equipo': item.tipoequipo,
      'Modelo': item.modelo,
      'Marca': item.marca,
      'Observación': item.observacion
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Asignaciones");
    XLSX.writeFile(workbook, `Reporte_Asignaciones_${new Date().toLocaleDateString()}.xlsx`);

    Swal.fire({
      icon: 'success',
      title: '¡Éxito!',
      text: 'Reporte generado correctamente',
      timer: 2000,
      showConfirmButton: false
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!fechaAsignacion || !observacion || !UsuarioId || !EquipoId) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor, completa todos los campos antes de guardar.',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const newAssignment = {
      fechaasignacion: fechaAsignacion,
      observacion,
      UsuarioId: UsuarioId.id,
      EquipoId: EquipoId.id,
    };

    dispatch(postasigequi(newAssignment))
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Asignación creada',
          text: 'Se ha creado la asignación de equipo con éxito.',
          confirmButtonColor: '#10b981'
        });

        setFechaAsignacion('');
        setObservacion('');
        setUsuarioId(null);
        setEquipoId(null);

        dispatch(getAllusuarios());
        dispatch(getEquipo());
      })
      .catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Hubo un problema al crear la asignación: ${error.message}`,
        });
      });
  };

  const asignacionColumns = [
    { field: 'equipoCod', headerName: 'Código', width: 120 },
    { field: 'usuarioNombre', headerName: 'Usuario', width: 180 },
    { field: 'fechaAsignacion', headerName: 'Asignado el', width: 150 },
    { field: 'serialEquipo', headerName: 'Serial', width: 130 },
    { field: 'tipoequipo', headerName: 'Tipo', width: 130 },
    { field: 'modelo', headerName: 'Modelo', width: 130 },
    { field: 'marca', headerName: 'Marca', width: 120 },
    { field: 'observacion', headerName: 'Observación', width: 250 },
  ];

  const asignacionesData = asignaciones
    .filter((asignacion) => asignacion.asignacion?.flag === 1)
    .map((asignacion, index) => ({
      id: `${asignacion.equipo?.id || index}-${asignacion.usuario?.id || index}`,
      equipoCod: asignacion.equipo?.cod || 'N/A',
      usuarioNombre: asignacion.usuario?.nombre || 'N/A',
      fechaAsignacion: asignacion.asignacion.fechaasignacion || 'N/A',
      serialEquipo: asignacion.equipo?.serial || 'N/A',
      tipoequipo: asignacion.tipoequipo || 'N/A',
      modelo: asignacion.equipo?.modelo || 'N/A',
      marca: asignacion.marca || 'N/A',
      observacion: asignacion.observacion || 'N/A',
    }));

  const filteredAsignaciones = asignacionesData.filter((item) =>
    String(item.equipoCod).toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.usuarioNombre).toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(item.serialEquipo).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>
          <FaUserPlus /> Asignación de Equipos
        </h2>
        <p className={styles.subtitle}>Vincula equipos a usuarios finales y gestiona el inventario activo.</p>
      </header>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={styles.formPaper}>
            <Typography className={styles.sectionTitle}>
              <FaClipboardList /> Registro de Asignación
            </Typography>

            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    className={styles.inputField}
                    label="Fecha de Asignación"
                    fullWidth
                    type="date"
                    value={fechaAsignacion}
                    onChange={(e) => setFechaAsignacion(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><FaCalendarAlt color="#94a3b8" /></InputAdornment>
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={allusuarios}
                    getOptionLabel={(option) => option.nombre || ''}
                    value={UsuarioId}
                    onChange={(event, newValue) => setUsuarioId(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccione un usuario"
                        className={styles.inputField}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start"><FaUser color="#94a3b8" /></InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={allequipos.map((e) => e.equipo)}
                    getOptionLabel={(option) => option.cod || ''}
                    value={EquipoId}
                    onChange={(event, newValue) => setEquipoId(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccione un equipo"
                        className={styles.inputField}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start"><FaLaptop color="#94a3b8" /></InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    className={styles.inputField}
                    label="Observación"
                    fullWidth
                    multiline
                    rows={2}
                    value={observacion}
                    onChange={(e) => setObservacion(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><FaCommentAlt color="#94a3b8" /></InputAdornment>
                    }}
                  />
                </Grid>

                <Grid item xs={12} style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
                  <Button type="submit" variant="contained" className={styles.submitBtn} startIcon={<FaSave />}>
                    Guardar Asignación
                  </Button>
                  <Button variant="outlined" className={styles.viewBtn} onClick={handleOpen} startIcon={<FaEye />}>
                    Ver Asignaciones
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        <Modal open={open} onClose={handleClose} closeAfterTransition>
          <Box className={styles.modalBox}>
            <div className={styles.modalHeader}>
              <Typography className={styles.modalTitle}>
                <FaClipboardList style={{ marginRight: '12px' }} /> Lista de Asignaciones
              </Typography>
              <IconButton onClick={handleClose} style={{ background: '#f1f5f9' }}>
                <CloseIcon />
              </IconButton>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center' }}>
              <TextField
                placeholder="Buscar por código, nombre o serial..."
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><FaSearch color="#3b82f6" /></InputAdornment>,
                  style: { borderRadius: '16px', background: '#f1f5f9', border: 'none' }
                }}
              />
              <Button
                variant="contained"
                onClick={handleExportExcel}
                style={{
                  background: '#10b981',
                  borderRadius: '16px',
                  padding: '12px 24px',
                  textTransform: 'none',
                  fontWeight: '700',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  gap: '8px',
                  minWidth: '180px'
                }}
              >
                <FaFileExcel size="1.2em" />
                Exportar Excel
              </Button>
            </div>

            <div className={styles.gridWrapper} style={{ height: 500 }}>
              <DataGrid
                rows={filteredAsignaciones}
                columns={asignacionColumns}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableSelectionOnClick
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                    fontWeight: '800',
                    borderBottom: '2px solid #e2e8f0'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#eff6ff',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f1f5f9',
                    color: '#1e293b',
                    fontWeight: '500'
                  }
                }}
              />
            </div>
          </Box>
        </Modal>
      </Grid>
    </div>
  );
};

export default FormularioAsignacionEquipo;
